/* Edited by Jonathan <jonathan@birubi.co> */
/*
  EV Stockist Logos.

  The design note on Figma 1408:1146 is the whole specification: "the row scrolls
  automatically only when the logos are wider than the viewport; with a few logos
  it sits still, left-aligned." So this element measures rather than assumes, and
  the server-rendered default is the still row -- if the script never runs, the
  section degrades to a plain left-aligned list of links, which is the correct
  state for the small stockist list the store starts with.

  Three modes, chosen in measure():
    (no attribute)  fits    -- still, left-aligned, gutter padding, no fades
    scroll          overflows, motion allowed -- cloned list, seamless loop
    swipe           overflows, reduced motion -- user-scrollable strip

  Duration is derived from a pixels-per-second setting rather than being a fixed
  number of seconds, so adding retailers lengthens the loop instead of speeding
  it up.
*/

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const DEFAULT_SPEED = 40; // px per second
const MEASURE_TOLERANCE = 1; // px, absorbs sub-pixel layout rounding

class EvStockistLogos extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-slogos-viewport]');
    this.list = this.querySelector('[data-slogos-list]');
    if (!this.viewport || !this.list) return;

    this.speed = parseFloat(this.dataset.speed) || DEFAULT_SPEED;
    this.motionQuery = window.matchMedia(REDUCED_MOTION);

    this.onPause = () => this.setPaused(true);
    this.onResume = () => this.setPaused(false);
    this.onMotionChange = () => this.measure();

    this.addEventListener('mouseenter', this.onPause);
    this.addEventListener('mouseleave', this.onResume);
    this.addEventListener('focusin', this.onPause);
    this.addEventListener('focusout', this.onResume);

    if (typeof this.motionQuery.addEventListener === 'function') {
      this.motionQuery.addEventListener('change', this.onMotionChange);
    }

    this.observeResize();
    this.observeVisibility();
    this.measure();
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onPause);
    this.removeEventListener('mouseleave', this.onResume);
    this.removeEventListener('focusin', this.onPause);
    this.removeEventListener('focusout', this.onResume);

    if (this.motionQuery && typeof this.motionQuery.removeEventListener === 'function') {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  observeResize() {
    if (typeof ResizeObserver !== 'function') return;

    /* Re-measured on a frame, because the observer fires per layout pass and
       measure() writes to the DOM -- reading and writing in the same callback
       is how you get an observer loop warning. */
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.measure();
      });
    });
    this.resizeObserver.observe(this);
  }

  observeVisibility() {
    if (typeof IntersectionObserver !== 'function') return;

    /* An animation nobody can see still occupies the compositor, so park it
       while the strip is off-screen. This rides on the same paused attribute
       as hover, which is why setPaused() ORs the two reasons together. */
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.offscreen = !entry.isIntersecting;
        this.applyPaused();
      });
    });
    this.visibilityObserver.observe(this);
  }

  setPaused(on) {
    this.hovered = on;
    this.applyPaused();
  }

  applyPaused() {
    if (this.hovered || this.offscreen) {
      this.dataset.slogosPaused = '';
    } else {
      delete this.dataset.slogosPaused;
    }
  }

  /* Width of the logos themselves, gaps included but gutter padding excluded.
     Measured from the items rather than from scrollWidth, because the list is a
     full-width block whose scrollWidth reports the container, not the content,
     whenever the row happens to fit. */
  contentWidth() {
    const items = Array.from(this.list.children);
    if (!items.length) return 0;

    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    return items.reduce((total, item, index) => {
      return total + item.getBoundingClientRect().width + (index ? gap : 0);
    }, 0);
  }

  measure() {
    if (!this.viewport || !this.list) return;

    // Back to the still row first, so every measurement is of the same layout.
    this.teardown();

    const listStyle = getComputedStyle(this.list);
    const gutters = (parseFloat(listStyle.paddingLeft) || 0) + (parseFloat(listStyle.paddingRight) || 0);
    const content = this.contentWidth();
    const available = this.clientWidth;

    if (!content || content + gutters <= available + MEASURE_TOLERANCE) return;

    if (this.motionQuery.matches) {
      this.dataset.slogosMode = 'swipe';
      return;
    }

    this.dataset.slogosMode = 'scroll';

    /* Read the gap only after the mode is on, because the scrolling layout puts
       the gap on the viewport (between the two list copies) rather than on the
       list, and the two values differ across the mobile breakpoint. */
    const seam = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
    const shift = content + seam;

    const clone = this.list.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-slogos-clone', '');
    clone.removeAttribute('data-slogos-list');
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    this.viewport.appendChild(clone);
    this.clone = clone;

    this.style.setProperty('--ev-slogos-shift', `${shift}px`);
    this.style.setProperty('--ev-slogos-duration', `${(shift / this.speed).toFixed(2)}s`);
  }

  teardown() {
    delete this.dataset.slogosMode;
    this.style.removeProperty('--ev-slogos-shift');
    this.style.removeProperty('--ev-slogos-duration');
    if (this.clone) {
      this.clone.remove();
      this.clone = null;
    }
  }
}

if (!customElements.get('ev-stockist-logos')) {
  customElements.define('ev-stockist-logos', EvStockistLogos);
}